import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PHONE_COUNTRIES, STORAGE_CUSTOMER_DATA } from 'src/app/core/constants';
import { RestaurantStore } from '../../../core/stores/restaurant.store';
import { PaymentStore, VerifyProcess } from '../../../core/stores/payment.store';
import { PaymentService } from '../../../core/services/payment.service';
import { Customer, CustomerData, CustomerToken, VerifyState } from '../../../core/models/customer.model';
import { CookieService } from 'ngx-cookie-service';
import { StorageService } from '../../../core/services/storage.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UtilService } from 'src/app/core/services/util.service';
import { PhoneFormat } from 'src/app/core/models/order.model';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-verify-phone-number',
  templateUrl: './verify-phone-number.component.html',
  styleUrls: ['./verify-phone-number.component.scss'],
})
export class VerifyPhoneNumberComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Output() getPreviousOrders = new EventEmitter();
  @Output() verificationCustomer = new EventEmitter();
  @Input() forceTouched: any;
  @Input() isUpdating: boolean;
  @Input() isSharedCardsEnabled: boolean;
  @Input() isNeedSharingPhoneNumber: boolean;
  @Input() isDisableQuickCheckout: boolean;
  @Output() setCustomerLoyaltyRewards: EventEmitter<Customer> = new EventEmitter<Customer>();
  @Output() saveCustomerData: EventEmitter<string> = new EventEmitter<string>();
  @Output() authoriseCustomer: EventEmitter<string> = new EventEmitter<string>();
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @Output() isPhoneValid: EventEmitter<boolean> = new EventEmitter<boolean>(true);

  public separateDialCode = false;
  public SearchCountryField = SearchCountryField;
  public PhoneNumberFormat = PhoneNumberFormat;
  public selectedCountry: CountryISO | string = CountryISO.UnitedStates;

  public phoneSubmitted: boolean;
  public phoneValid: boolean;
  public codeFailed: boolean;
  public codeSent: boolean;
  public allowResent: boolean;
  public startSent: boolean;
  public phoneGroup: FormGroup;
  private lastValidPhoneNumber = '';
  private ERROR_MESSAGE = 'Please provide a valid phone number';
  public allowCountries: CountryISO[] = PHONE_COUNTRIES;
  private destroyed$ = new Subject<void>();
  private customer: CustomerData = new CustomerData();

  public get isStoneCreek(): boolean {
    return this.utilService.isStoneCreek();
  }

  public get controls(): {
    [key: string]: AbstractControl;
  } {
    return this.phoneGroup.controls;
  }

  public get allowSendVerifyCode(): boolean {
    return (this.phoneSubmitted && !!this.paymentStore.customer && !this.codeSent) || this.paymentStore.customer?.cards || this.allowResent;
  }

  public get enableVerifyButton(): boolean {
    return (this.verifyButtonText === VerifyState.RESENT_CODE || this.verifyButtonText === VerifyState.SEND_CODE || this.verifyButtonText === VerifyState.VERIFY_PHONE) && !this.startSent;
  }

  public get verifyButtonText(): string {
    if (this.userVerified) {
      return VerifyState.VERIFIED;
    } else if (this.allowResent) {
      return VerifyState.RESENT_CODE;
    } else if (this.codeSent || this.startSent) {
      return VerifyState.CODE_SENT;
    } else if (this.phoneSubmitted) {
      return this.isStoneCreek ? VerifyState.VERIFY_PHONE : VerifyState.SEND_CODE;
    }
    return '';
  }

  public get verifyButtonTextStyle(): string {
    if (this.userVerified) {
      return 'phone__input-button-verified';
    } else if ((this.codeSent && !this.allowResent) || (!this.codeSent && !this.phoneSubmitted) || this.startSent) {
      return 'phone__input-button-send';
    } else if (this.phoneSubmitted || this.allowResent) {
      return 'phone__input-button-resent';
    }
    return '';
  }

  public get needVerification(): boolean {
    return !this.paymentStore.verificationCustomer && this.codeSent && !this.startSent;
  }

  public get isActiveCustomerWithCards(): boolean {
    return this.paymentStore.customer && this.paymentStore.customer?.cards && this.paymentStore.customer?.cards.length > 0;
  }

  public get userVerified(): boolean {
    return this.paymentStore.verificationCustomer;
  }

  public get showVerifyButton(): boolean {
    return this.phoneValid || (this.paymentStore.customer?.cards && this.paymentStore.customer?.cards.length > 0) || this.isActiveCustomerWithCards;
  }

  public get error(): AbstractControl {
    return this.phoneGroup.get('phone');
  }

  public get usersPhoneNumber() {
    return this.paymentStore.phoneNumber;
  }

  public set usersPhoneNumber(value: any) {
    this.paymentStore.setPhoneNumber(value.nationalNumber);
    this.updatePhoneNumberValue(value.nationalNumber);
  }

  public errorMessage = this.ERROR_MESSAGE;

  constructor(
    public cookieService: CookieService,
    public paymentService: PaymentService,
    public paymentStore: PaymentStore,
    private storageService: StorageService,
    private restaurantStore: RestaurantStore,
    private formBuilder: FormBuilder,
    private utilService: UtilService
  ) {}

  ngOnInit() {
    this.setDefaultCountryCode();
    this.initCustomer();
    this.initPhoneForm();
  }

  private async initCustomer(): Promise<void> {
    const customerStorageData = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (customerStorageData) {
      this.customer = customerStorageData;
      const { token, phone } = customerStorageData;
      if (phone && token) {
        this.paymentStore.verifyCustomerState = VerifyProcess.VERIFYING;
        this.paymentStore.setPhoneNumber(phone);
        this.isPhoneValid.emit(true);
        this.controls.phone.setValue(phone);
        this.paymentService
          .getVerifiedCustomerByPhone(phone, token, this.isSharedCardsEnabled, this.isNeedSharingPhoneNumber)
          .subscribe((customer: Customer) => {
            this.paymentStore.customer = customer;
            this.paymentStore.verificationCustomer = true;
            this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
            this.lastValidPhoneNumber = phone;
            this.saveCustomerInfoToStore();
            this.setCustomerLoyaltyRewards.emit(customer);
            this.authoriseCustomer.emit(token);
            this.verificationCustomer.emit();
            this.checkPhone();
          }, () => {
            this.getCustomerByPhone(phone);
          });
        return;
      } else if (phone) {
        this.paymentStore.setPhoneNumber(phone);
        this.controls.phone.setValue(phone);
        this.checkPhone();
        return;
      }
    }
    this.checkPhone();
  }

  private saveCustomerInfoToStore(): void {
    if (this.paymentStore.customer.givenName) {
      this.paymentStore.setGivenName(this.paymentStore.customer.givenName);
    }
    if (this.paymentStore.customer.familyName) {
      this.paymentStore.setFamilyName(this.paymentStore.customer.familyName);
    }
    if (!this.paymentStore.email) {
      this.paymentStore.setEmail(this.paymentStore.customer.emailAddress);
    }
    if (!this.paymentStore.billingAddress) {
      this.paymentStore.setBillingAddress(this.paymentStore.customer.address);
    }
  }

  public getCustomerByPhone(phone: string, sendVerificationCode: boolean = false): void {
    this.startSent = true;
    this.paymentStore.verificationCustomer = false;
    this.paymentStore.verifyCustomerState = VerifyProcess.VERIFYING;
    this.phoneGroup.disable();
    let isNeedSharingPhoneNumber = this.isNeedSharingPhoneNumber;
    if (this.paymentStore.createTabChecked && this.restaurantStore.group && !this.paymentStore.customer) {
      isNeedSharingPhoneNumber = true;
    }
    this.paymentService.getCustomerByPhone(phone, this.isSharedCardsEnabled, isNeedSharingPhoneNumber).subscribe(
      (customer: Customer) => {
        this.phoneGroup.enable();
        this.startSent = false;
        this.paymentStore.verifyCustomerState = VerifyProcess.SUCCESS;
        this.paymentStore.customer = customer;
        this.phoneSubmitted = true;
        this.cookieService.set('phoneNumber', customer.phone);
        this.setCustomerLoyaltyRewards.emit(customer);
        this.validChange.emit(true);
        this.checkPhone();
        if (sendVerificationCode) {
          this.verificationCode(customer.orderBotUserId);
        }
        if (!customer.loyaltyRewardsTier || customer.loyaltyRewardsTier?.length === 0) {
          this.paymentStore.selectedLoyaltyReward = null;
          this.paymentStore.useLoyaltyChecked = false;
        }
        if (this.isDisableQuickCheckout) {
          if (this.paymentStore.customer.cards) {
            this.paymentStore.customer.cards = null;
          }
          this.paymentStore.verificationCustomer = true;
          return;
        }
        this.paymentStore.verificationCustomer = false;
      },
      () => {
        this.checkPhone();
        this.phoneGroup.enable();
      }
    );
  }

  public getVerificationCode() {
    if (!this.paymentStore.customer?.orderBotUserId) {
      this.getCustomerByPhone(this.paymentStore.phoneNumber, true);
      return;
    }
    this.verificationCode(this.paymentStore.customer.orderBotUserId);
  }

  public sendVerifyingCode(verificationCode: string) {
    this.codeFailed = false;
    this.allowResent = false;
    this.paymentService.sendVerifyingCode(this.paymentStore.customer.orderBotUserId, verificationCode).subscribe(
      (result: CustomerToken) => {
        const prevCustomer = this.paymentStore.customer
        this.paymentStore.customer = result.customer;
        this.paymentStore.customer.loyaltyPointsBalance = prevCustomer.loyaltyPointsBalance
        this.paymentStore.customer.loyaltyAccountId = prevCustomer.loyaltyAccountId
        this.paymentStore.customer.loyaltyRewardsTier = prevCustomer.loyaltyRewardsTier
        this.codeSent = false;
        this.allowResent = false;
        this.paymentStore.verificationCustomer = true;
        this.fillPersonalInfo(this.paymentStore.customer, result.customer);
        this.saveCustomerInfoToStore();
        this.storageService.set(
          STORAGE_CUSTOMER_DATA,
          new CustomerData(
            result.token,
            this.paymentStore.customer.phone,
            this.paymentStore.customer.orderBotUserId
          )
        );
        this.saveCustomerData.emit(result.token);
        this.getPreviousOrders.emit();
        this.verificationCustomer.emit(true);
      },
      () => {
        this.allowResent = true;
        this.codeFailed = true;
      }
    );
  }

  public fillPersonalInfo(customer: Customer, verifiedCustomer: Customer) {
    customer.givenName = verifiedCustomer.givenName;
    customer.familyName = verifiedCustomer.familyName;
    customer.emailAddress = verifiedCustomer.emailAddress;
    customer.address = verifiedCustomer.address;
  }

  private updatePhoneNumberValue(value: string): void {
    this.phoneValid = false;
    this.allowResent = false;
    if (this.controls.phone.valid && value !== this.lastValidPhoneNumber) {
      this.lastValidPhoneNumber = value;
      this.codeSent = false;
      this.phoneValid = true;
      this.getCustomerByPhone(this.usersPhoneNumber);
    } else if (value === this.lastValidPhoneNumber) {
      this.phoneValid = true;
      this.phoneSubmitted = true;
    }
  }

  private initPhoneForm(): void {
    this.phoneGroup = this.formBuilder.group({
      phone: new FormControl(undefined, [Validators.required]),
    });
  }

  private checkPhone(): void {
    this.controls.phone.valueChanges.subscribe((value: PhoneFormat) => {
      if (this.controls.phone.valid && value) {
        this.paymentStore.setPhoneNumber(value.e164Number);
        this.updatePhoneNumberValue(value.e164Number);
        this.isPhoneValid.emit(true);
        return;
      }
      this.isPhoneValid.emit(false);
      this.allowResent = false;
      this.phoneSubmitted = false;
    });
  }

  private setDefaultCountryCode(): void {
    this.restaurantStore.restaurantAvailableStatus$.subscribe((restaurant) => {
      if (restaurant?.country) {
        this.selectedCountry = restaurant.country;
        return;
      }
      this.selectedCountry = CountryISO.UnitedStates;
    });
  }

  public allowResendCode(): void {
    this.paymentStore.verificationCustomer = false;
    this.forceTouched = false;
    this.codeSent = false;
    this.phoneValid = true;
    this.phoneSubmitted = true;
    this.startSent = false;
    this.getVerificationCode();
  }

  private verificationCode(botUserId: string): void {
    if (this.allowSendVerifyCode) {
      this.startSent = true;
      this.allowResent = false;
      this.paymentService.getVerifyingCode(botUserId).subscribe(
        () => {
          this.codeSent = true;
          this.startSent = false;
          this.allowResent = false;
          setTimeout(() => (this.allowResent = true), 3000);
        },
        () => {
          this.startSent = false;
        }
      );
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }
}
